import { Box, MessageBlock } from '@deckee/deck-hand';
import {
  BoatingRiskRating,
  classifyHour,
  sortByNearestDate,
  WaterBody,
} from '@deckee/utilities';
import { DateTime } from 'luxon';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { useTheme } from 'styled-components';
import { callEndpoint, telemetry } from '../../../../../services/backend';
import { captureEvent } from '../../../../../services/event-capture';
import { hasFeatureEnabled } from '../../../../../services/feature';
import { useAppDispatch } from '../../../../../services/store';
import {
  postMemberSettings,
  selectRiskForecastConfig,
  selectRiskForecastVesselLength,
  selectRiskForecastWatercraftId,
  selectRiskForecastWatercraftType,
} from '../../../../../services/store/modules/app-controls';
import { selectSelectedVessel } from '../../../../../services/store/modules/vessels';
import useVessels from '../../../Vessels/hooks/use-vessels';
import { getVesselLengthInMeters } from '../../../Vessels/utils/vessel-length';
import SheetModal from '../../../../shared/SheetModal/SheetModal';
import LogoSpinner from '../../../../shared/_deprecated/components/LogoSpinner/LogoSpinner';
import { NUMBER_OF_DAYS_TO_REQUEST } from '../../constants/graph';
import {
  CLEAR_SKY_WEATHER_CODE,
  ForecastAnimation,
} from '../ForecastAnimation';
import RiskForecastDescription from './RiskForecastDescription';
import RiskForecastDebugPanel from './RiskForecastDebugPanel';
import RiskForecastInfoPanel from './RiskForecastInfoPanel';
import RiskForecastWatercraftSheet from './RiskForecastWatercraftSheet';
import { safelyFillHourlyGapsForWeatherGraphType } from '../../helpers/graph/graphData';
import { useGraphTime } from '../GraphCards/GraphTimeContext';
import { getRiskForecastLevelDisplay } from './getRiskForecastLevelDisplay';
import {
  buildBoatingForecastPoints,
  filterGraphPointsForDate,
  riskLevelForRating,
  type WeatherGraphPoint,
} from '../../helpers/risk/riskLevels';
import { resolveVesselProfile } from '../../helpers/risk/vesselProfileSelection';
import { resolveWaterBody } from '../../helpers/risk/waterBody';
import RiskScrubGraph from './RiskScrubGraph';
import CollapsibleCard from '../CollapsibleCard/CollapsibleCard';

interface RiskForecastProps {
  latitude: number;
  longitude: number;
  selectedDate: string;
  /** Daily outlook icon (`weatherPrecis*`) for the selected day — drives the animation sky. */
  weatherIcon?: string;
  days?: number;
}

interface GraphPoint {
  y: number;
  x: string;
}

const RiskForecast: FC<RiskForecastProps> = ({
  latitude,
  longitude,
  selectedDate,
  weatherIcon,
  days = NUMBER_OF_DAYS_TO_REQUEST,
}) => {
  let riskLevelLabel;

  const theme = useTheme();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const location = useLocation();

  const {
    selectedGraphTime,
    selectedChartTime,
    defaultChartTime,
    handleSelectedChartTimeChange,
    handleChartScrubStart,
    handleChartScrubEnd,
  } = useGraphTime('boating forecast');
  const [dataLocation, setDataLocation] = useState<
    | {
        distance: number;
        distanceUnits: string;
        name: string;
        timeZone: string;
        utcOffset: number;
      }
    | undefined
  >();

  const [selectedUtcTime, setSelectedUtcTime] = useState<string>('');
  const [swellData, setSwellData] = useState<WeatherGraphPoint[]>([]);
  const [swellPeriodData, setSwellPeriodData] = useState<WeatherGraphPoint[]>(
    [],
  );
  const [windData, setWindData] = useState<WeatherGraphPoint[]>([]);
  const [editingWatercraft, setEditingWatercraft] = useState(false);
  const [watercraftSheetMode, setWatercraftSheetMode] = useState<
    'select' | 'manual'
  >('select');
  const [savingVesselLength, setSavingVesselLength] = useState(false);
  const [errorSavingVesselLength, setErrorSavingVesselLength] =
    useState<boolean>(false);

  const savedVesselLength = useSelector(selectRiskForecastVesselLength);
  const savedRiskForecastConfig = useSelector(selectRiskForecastConfig);
  const savedWatercraftId = useSelector(selectRiskForecastWatercraftId);
  const savedWatercraftType = useSelector(selectRiskForecastWatercraftType);
  const selectedVessel = useSelector(selectSelectedVessel);
  const { vessels, selectVessel, clearVesselSelection } = useVessels();

  const vesselLength = useMemo(() => {
    const selectedVesselLength = selectedVessel
      ? getVesselLengthInMeters(selectedVessel)
      : undefined;
    return selectedVesselLength ?? savedVesselLength;
  }, [savedVesselLength, selectedVessel]);

  /**
   * The watercraft type comes from the saved vessel when one is selected, and
   * otherwise from the manually saved type. Both live outside this component so
   * every day of the forecast rates against the same watercraft: each day is its
   * own instance of this card inside the swipe pager.
   */
  const vessel = useMemo(
    () =>
      resolveVesselProfile(
        selectedVessel?.watercraft_type ?? savedWatercraftType,
        vesselLength,
      ),
    [savedWatercraftType, selectedVessel, vesselLength],
  );

  const timeZone = dataLocation?.timeZone;

  /**
   * Coast or inland is a property of the LOCATION, so it is decided from the
   * whole swell series rather than the single day on screen: a day whose swell
   * readings happen to be missing must not flip a coastal spot to inland.
   */
  const waterBody = useMemo(() => resolveWaterBody(swellData), [swellData]);

  const filteredWindData = useMemo(() => {
    if (!timeZone) {
      return [];
    }

    return filterGraphPointsForDate(windData, selectedDate, timeZone);
  }, [selectedDate, timeZone, windData]);

  const filteredSwellData = useMemo(() => {
    if (!timeZone) {
      return [];
    }

    return filterGraphPointsForDate(swellData, selectedDate, timeZone);
  }, [selectedDate, swellData, timeZone]);

  const filteredSwellPeriodData = useMemo(() => {
    if (!timeZone) {
      return [];
    }

    return filterGraphPointsForDate(swellPeriodData, selectedDate, timeZone);
  }, [selectedDate, swellPeriodData, timeZone]);

  const windAndSwellData = useMemo(
    () =>
      buildBoatingForecastPoints(vessel, waterBody, {
        wind: filteredWindData,
        swellHeight: filteredSwellData,
        swellPeriod: filteredSwellPeriodData,
      }),
    [
      filteredSwellData,
      filteredSwellPeriodData,
      filteredWindData,
      vessel,
      waterBody,
    ],
  );

  useEffect(() => {
    if (!savedWatercraftId || selectedVessel || vessels.length === 0) {
      return;
    }

    const savedVessel = vessels.find(
      (vessel) => Number(vessel.id) === Number(savedWatercraftId),
    );
    if (savedVessel) {
      selectVessel(savedVessel);
    }
  }, [savedWatercraftId, selectedVessel, vessels, selectVessel]);

  useEffect(() => {
    if (editingWatercraft) {
      setWatercraftSheetMode('select');
    }
  }, [editingWatercraft]);

  const getClosestPoint = useCallback(
    <TPoint extends GraphPoint>(
      points: TPoint[],
      selectedTime: string,
    ): TPoint | undefined => {
      const pointsToSort = [...points];
      pointsToSort.sort(
        sortByNearestDate(
          new Date(selectedTime),
          (obj: { x: string | number | Date }) => new Date(obj.x),
        ),
      );
      return pointsToSort[0];
    },
    [],
  );

  const dataEndpoint = 'queryWeatherForecastGraphData';
  const swellDataType = 'swell-height';
  const swellPeriodDataType = 'swell-period';
  const windDataType = 'wind';

  useEffect(() => {
    let isMounted = true;

    setWindData([]);
    setSwellData([]);
    setSwellPeriodData([]);
    setDataLocation(undefined);

    const queryWeatherSummaryParams = {
      latitude,
      longitude,
    };

    const fetchWeatherData = async (
      dataType: string,
      storeAction: React.Dispatch<React.SetStateAction<WeatherGraphPoint[]>>,
    ): Promise<void> => {
      try {
        const fetchedWeatherData = await callEndpoint(
          callEndpoint.endpoints[dataEndpoint],
          {},
          {
            ...queryWeatherSummaryParams,
            days,
            type: dataType,
          },
        );

        if (!isMounted) {
          return;
        }

        const rawPoints = fetchedWeatherData?.points ?? [];

        storeAction(
          safelyFillHourlyGapsForWeatherGraphType(
            rawPoints,
            dataType,
            fetchedWeatherData?.location?.timeZone,
          ),
        );

        if (fetchedWeatherData?.location?.timeZone) {
          setDataLocation(fetchedWeatherData.location);
        }
      } catch (fetchError) {
        telemetry(
          telemetry.ERROR,
          `[RiskForecast.fetchWeatherData] ${dataType} failed: ${JSON.stringify(fetchError)}`,
        );

        if (!isMounted) {
          return;
        }

        storeAction([]);
      }
    };

    void Promise.all([
      fetchWeatherData(swellDataType, setSwellData),
      fetchWeatherData(swellPeriodDataType, setSwellPeriodData),
      fetchWeatherData(windDataType, setWindData),
    ]);

    return () => {
      isMounted = false;
    };
  }, [days, latitude, longitude]);

  useEffect(() => {
    if (!selectedGraphTime) {
      return;
    }

    const selectedGraphTimeUtc = DateTime.fromISO(selectedGraphTime)
      .toUTC()
      .toISO();

    setSelectedUtcTime(selectedGraphTimeUtc ?? '');
  }, [selectedGraphTime]);

  let selectedDataPoint;
  let selectedRating: BoatingRiskRating | undefined;
  if (filteredWindData?.length) {
    selectedDataPoint = getClosestPoint(windAndSwellData, selectedUtcTime);

    if (selectedDataPoint) {
      selectedRating = classifyHour(
        selectedDataPoint.conditions,
        vessel,
        waterBody,
      );
      riskLevelLabel = riskLevelForRating(selectedRating);
    }
  }

  const handleStartEditingWatercraft = (from: string) => {
    captureEvent({
      type: 'Risk forecast: edit vessel length click',
      payload: { from },
      sendToAmplitude: true,
    });
    setEditingWatercraft(true);
  };

  const persistRiskForecastConfig = async ({
    vesselLength: newVesselLength,
    watercraftId,
    watercraftType,
  }: {
    vesselLength: number;
    watercraftId?: number | string | null;
    watercraftType?: string | null;
  }): Promise<boolean> => {
    setSavingVesselLength(true);
    setErrorSavingVesselLength(false);

    const normalizedWatercraftId =
      watercraftId != null && watercraftId !== '' ? Number(watercraftId) : null;

    try {
      await dispatch(
        postMemberSettings({
          riskForecastConfig: {
            ...savedRiskForecastConfig,
            vesselLength: newVesselLength,
            watercraftId: normalizedWatercraftId,
            watercraftType: watercraftType ?? null,
          },
        }),
      ).unwrap();
      return true;
    } catch {
      setErrorSavingVesselLength(true);
      return false;
    } finally {
      setSavingVesselLength(false);
    }
  };

  const handleDismissWatercraftSheet = () => {
    captureEvent({
      type: 'Risk forecast: dismiss watercraft sheet',
      payload: {},
      sendToAmplitude: true,
    });
    setEditingWatercraft(false);
  };

  const handleSelectWatercraft = async (vessel: (typeof vessels)[number]) => {
    captureEvent({
      type: 'Risk forecast: select watercraft',
      payload: { vesselId: vessel.id },
      sendToAmplitude: true,
    });

    const lengthInMeters = getVesselLengthInMeters(vessel);
    if (!lengthInMeters) {
      setWatercraftSheetMode('manual');
      return;
    }

    // The saved vessel carries its own type, so the manual one is dropped.
    const success = await persistRiskForecastConfig({
      vesselLength: lengthInMeters,
      watercraftId: vessel.id,
      watercraftType: null,
    });
    if (success) {
      selectVessel(vessel);
      setEditingWatercraft(false);
    }
  };

  const handleManualSave = async (
    newVesselLength: number,
    watercraftType?: string,
  ) => {
    const success = await persistRiskForecastConfig({
      vesselLength: newVesselLength,
      watercraftId: null,
      watercraftType: watercraftType ?? null,
    });
    if (success) {
      clearVesselSelection();
      setEditingWatercraft(false);
    }
  };

  const handleAddWatercraft = () => {
    captureEvent({
      type: 'Risk forecast: add watercraft click',
      payload: {},
      sendToAmplitude: true,
    });
    setEditingWatercraft(false);
    history.push('/maps/settings/account/boats/new', {
      returnUrl: `${location.pathname}${location.search}`,
    });
  };

  const hasRiskCalculationData = filteredWindData?.length > 0;
  const riskLevelDisplay = getRiskForecastLevelDisplay(riskLevelLabel, theme);

  const isForecastAnimationEnabled = hasFeatureEnabled('forecast-animation');
  const isRiskForecastDebugEnabled = hasFeatureEnabled('risk-forecast-debug');

  /** Fractional hour of the scrubbed moment, in the location's timezone. */
  const selectedHour = useMemo(() => {
    if (!selectedGraphTime) {
      return 12;
    }

    const moment = timeZone
      ? DateTime.fromISO(selectedGraphTime, { zone: timeZone })
      : DateTime.fromISO(selectedGraphTime);

    return moment.isValid ? moment.hour + moment.minute / 60 : 12;
  }, [selectedGraphTime, timeZone]);

  return (
    <CollapsibleCard
      title={
        waterBody === WaterBody.Inland
          ? 'Enclosed waters boating forecast'
          : 'Coastal boating forecast'
      }
      testId="weather-forecast-risk-forecast"
      analyticsCard="boating forecast"
    >
      <RiskForecastInfoPanel
        vesselLength={vesselLength}
        selectedVessel={selectedVessel}
        watercraftType={savedWatercraftType ?? undefined}
        onClickEdit={() => handleStartEditingWatercraft('info panel')}
      />

      {isForecastAnimationEnabled &&
        hasRiskCalculationData &&
        selectedDataPoint &&
        selectedRating && (
          <Box mt={3} width="100%" borderRadius="8px" overflow="hidden">
            <ForecastAnimation
              conditions={selectedDataPoint.conditions}
              vessel={vessel}
              waterBody={waterBody}
              rating={selectedRating}
              hour={selectedHour}
              // Daily outlook icons are normalized for WillyWeather and WWO.
              weatherCode={weatherIcon ?? CLEAR_SKY_WEATHER_CODE}
            />
          </Box>
        )}

      {hasRiskCalculationData &&
        selectedDataPoint &&
        selectedDataPoint.wind != null &&
        riskLevelDisplay && (
          <Box
            fontSize="18px"
            fontWeight="bold"
            color={riskLevelDisplay.color}
            mb={2}
            mt={3}
          >
            {riskLevelDisplay.label}
          </Box>
        )}

      {hasRiskCalculationData &&
        selectedDataPoint &&
        selectedRating &&
        selectedDataPoint.wind != null && (
          <RiskForecastDescription
            selectedGraphTime={selectedGraphTime}
            conditions={selectedDataPoint.conditions}
            graphic={vessel.graphic}
            rating={selectedRating}
            waterBody={waterBody}
            latitude={latitude}
            longitude={longitude}
            selectedDate={selectedDate}
            locationTimezone={dataLocation?.timeZone}
            riskLevel={riskLevelLabel}
            hideHeader
          />
        )}

      {isRiskForecastDebugEnabled &&
        hasRiskCalculationData &&
        selectedDataPoint &&
        selectedRating && (
          <RiskForecastDebugPanel
            conditions={selectedDataPoint.conditions}
            vessel={vessel}
            waterBody={waterBody}
            rating={selectedRating}
            selectedTime={selectedGraphTime}
          />
        )}

      <Box mt={3} width="100%">
        {timeZone && windAndSwellData.length > 0 ? (
          <RiskScrubGraph
            riskPoints={windAndSwellData}
            selectedDate={selectedDate}
            timeZone={timeZone}
            selectedTime={selectedChartTime ?? undefined}
            defaultSelectedTime={defaultChartTime}
            onSelectedTimeChange={handleSelectedChartTimeChange}
            onScrubStart={handleChartScrubStart}
            onScrubEnd={handleChartScrubEnd}
          />
        ) : null}
      </Box>

      <SheetModal
        isOpen={editingWatercraft}
        onClose={handleDismissWatercraftSheet}
      >
        <Box width="100vw" maxWidth="450px" p="3">
          {errorSavingVesselLength && (
            <MessageBlock type="error" mb="3">
              Sorry, an unexpected error occurred! Please try again.
            </MessageBlock>
          )}
          {savingVesselLength ? (
            <LogoSpinner />
          ) : (
            <RiskForecastWatercraftSheet
              mode={watercraftSheetMode}
              onModeChange={setWatercraftSheetMode}
              vessels={vessels}
              vesselLength={vesselLength}
              watercraftType={savedWatercraftType ?? undefined}
              onSelectVessel={handleSelectWatercraft}
              onAddWatercraft={handleAddWatercraft}
              onManualSave={handleManualSave}
            />
          )}
        </Box>
      </SheetModal>
    </CollapsibleCard>
  );
};

export default RiskForecast;
